// vue core
import Vue from 'vue'

// api
import { api } from '@/services'

// libs
import qs from 'qs'

// utils
import { errorMessages } from '@/util/helpers'
import { createCancelPreviousRequest, isCancelError } from '@/util/cancelPreviousRequest'

// services
import * as appointmentService from '@/services/appointment/appointmentService'
import { getSignedUrl, uploadToS3 } from '@/services/upload/uploadService'

// constants
const WEB_APPOINTMENT_URL = '/web/appointment'
const ADMIN_APPOINTMENT_URL = '/admin/appointment'
const OPEN_APPOINTMENT_URL = '/web/open/appointment'

// request control
const fetchAppointmentsLatestRequest = createCancelPreviousRequest()
const fetchCalendarAppointmentsLatestRequest = createCancelPreviousRequest()

// helpers
const sortAlphabetically = (data, key) => {
  return data.sort((itemA, itemB) => itemA[key] > itemB[key] ? 1 : -1)
}

const setOtherOptionToEnd = (array) => {
  let newArray = array

  const otherIndex = newArray.findIndex((reason) => reason.id == '21')
  const other = newArray.splice(otherIndex, 1)[0]
  newArray.push(other)

  return newArray
}

// vuex module
const state = {
  appointment: {},
  appointments: [],
  appointmentTypes: [],
  realEstateAvailableServiceTypes: [],
  cancelReasons: [],
  batchAppointments: [],
  batchAppointmentsWithError: [],
  appointmentCreationConfirmToClose: false,
  isAppointmentLoading: false,
  isVisitorUpdating: false,
  isFciUpdating: false,
  isTermUploading: false,
  isAppointmentCanceling: false,
  confirmAppointmentData: {},
  temporaryFile: {},
  checklistLinkedAppointmentTypes: [],
  calendarAppointments: [],
  isCalendarLoading: false,
  workers: { hosts: [], internals: [], thirdParties: [] },
}

const getters = {
  appointment: (state) => state.appointment,
  appointments: (state) => state.appointments,
  appointmentTypes: (state) => state.appointmentTypes,
  realEstateAvailableServiceTypes: (state) => state.realEstateAvailableServiceTypes,
  cancelReasons: (state) => state.cancelReasons,
  batchAppointments: (state) => state.batchAppointments,
  batchAppointmentsWithError: (state) => state.batchAppointmentsWithError,
  appointmentCreationConfirmToClose: (state) => state.appointmentCreationConfirmToClose,
  isAppointmentLoading: (state) => state.isAppointmentLoading,
  isVisitorUpdating: (state) => state.isVisitorUpdating,
  isFciUpdating: (state) => state.isFciUpdating,
  isTermUploading: (state) => state.isTermUploading,
  isAppointmentCanceling: (state) => state.isAppointmentCanceling,
  confirmAppointmentData: (state) => state.confirmAppointmentData,
  temporaryFile: (state) => state.temporaryFile,
  checklistLinkedAppointmentTypes: (state) => state.checklistLinkedAppointmentTypes,
  calendarAppointments: (state) => state.calendarAppointments,
  isCalendarLoading: (state) => state.isCalendarLoading,
  workers: (state) => state.workers,
}

const actions = {
  async fetchAppointment({ commit, dispatch }, id) {
    commit('setAppointment', {})
    commit('setIsAppointmentLoading', true)

    try {
      const { data } = await api.get(`${WEB_APPOINTMENT_URL}/show/${id}`)

      commit('setAppointment', data)
    } catch ({ data }) {
      dispatch('showErrorMessage', data.message)
    } finally {
      commit('setIsAppointmentLoading', false)
    }
  },

  async fetchAppointments({ commit, dispatch, rootState }) {
    const { cancelToken, isLatest } = fetchAppointmentsLatestRequest.start(
      'Canceled because a newer fetchAppointments started'
    )

    commit('setIsLoading', true)
    const defaultParams = {
      size: 30,
      page: rootState.paginate.currentPage - 1,
    }
    const params = { ...defaultParams, ...rootState.filters.filters }

    try {
      const { data } = await api.get(`${WEB_APPOINTMENT_URL}/list`, {
        cancelToken,
        params,
        paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
      })

      if (!isLatest()) return

      const { result, total } = data

      commit('setAppointments', result)
      commit('setTotalPages', total)
    } catch (error) {
      if (isCancelError(error)) return
      if (!isLatest()) return

      if (typeof error === 'string') {
        dispatch('showErrorMessage', error)
        return
      }

      const data = error?.data ?? error?.response?.data

      data?.errors && data?.errors?.length >= 1
        ? dispatch('showErrorMessage', data.errors[0].message)
        : dispatch('showErrorMessage', data?.message)
    } finally {
      if (isLatest()) {
        commit('setIsLoading', false)
      }
    }
  },

  // Isolated fetch for the calendar panel on the creation screen.
  // Does not use global filters nor write to `appointments` (board state).
  async fetchCalendarAppointments({ commit, dispatch }, { startDate, endDate, hostIds, realtyIds } = {}) {
    const { cancelToken, isLatest } = fetchCalendarAppointmentsLatestRequest.start(
      'Canceled because a newer fetchCalendarAppointments started'
    )

    commit('setCalendarLoading', true)

    const params = {
      size: 200,
      page: 0,
      status: ['DRAFT', 'OPEN', 'SCHEDULED'],
      startDate,
      endDate,
    }

    if (hostIds && hostIds.length) params.hostIds = hostIds
    if (realtyIds && realtyIds.length) params.realtyIds = realtyIds

    try {
      const { data } = await api.get(`${WEB_APPOINTMENT_URL}/list`, {
        cancelToken,
        params,
        paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
      })

      if (!isLatest()) return

      commit('setCalendarAppointments', data.result)
    } catch (error) {
      if (isCancelError(error)) return
      if (!isLatest()) return

      if (typeof error === 'string') {
        dispatch('showErrorMessage', error)
        return
      }

      const data = error?.data ?? error?.response?.data

      data?.errors && data?.errors?.length >= 1
        ? dispatch('showErrorMessage', data.errors[0].message)
        : dispatch('showErrorMessage', data?.message)
    } finally {
      if (isLatest()) {
        commit('setCalendarLoading', false)
      }
    }
  },

  async fetchWorkers({ commit, dispatch }) {
    try {
      const { data } = await appointmentService.getWorkers()
      commit('setWorkers', data)
    } catch (error) {
      const data = error?.data ?? error?.response?.data
      dispatch('showErrorMessage', data?.message)
    }
  },

  async fetchAppointmentTypes({ commit, dispatch }) {
    try {
      const { data } = await api.get(`${WEB_APPOINTMENT_URL}/serviceTypes`)
      commit('setAppointmentTypes', sortAlphabetically(data, 'name'))
    } catch ({ data }) {
      dispatch('showErrorMessage', data.message)
    }
  },

  async fetchRealEstateAvailableServiceTypes({ commit, dispatch }, realEstateId) {
    try {
      let url = `${WEB_APPOINTMENT_URL}/realEstateServiceTypes`
      if (realEstateId) {
        url += `?realEstateId=${realEstateId}`
      }
      const { data } = await api.get(url)
      commit('setRealEstateAvailableServiceTypes', sortAlphabetically(data, 'name'))
    } catch ({ data }) {
      dispatch('showErrorMessage', data.message)
    }
  },

  async cancelAppointment({ commit, dispatch }, cancelData) {
    const { id, cancelReason, reasonCancelId, form } = cancelData

    commit('setIsLoading', true)

    await api
      .post(`${WEB_APPOINTMENT_URL}/cancel/${id}`, {
        cancel_reason: cancelReason,
        reason_cancel_id: reasonCancelId,
      })
      .then((response) => {
        commit('setIsLoading', false)
        if (response.status === 200) {
          form.reset()

          dispatch('fetchBoard')
          commit('closeAllModals')
          dispatch('showSuccessMessage', Vue.i18n.translate('appointment.messages.successfullyCanceled'))
        }
      })
      .catch(({ data }) => {
        commit('setIsLoading', false)

        data?.errors && data?.errors?.length >= 1
          ? dispatch('showErrorMessage', data.errors[0].message)
          : dispatch('showErrorMessage', data.message)
      })
  },

  async cancelAppointmentAsAdmin({ commit, dispatch }, cancelData) {
    commit('setAppointmentIsCanceling', true)

    const payload = {
      appointment_id: cancelData?.id,
      real_estate_id: cancelData?.realEstateId,
      notify_property_manager: cancelData?.notifyPropertyManager,
      cancel_request: {
        reason_cancel_id: cancelData.reasonCancelId,
        cancel_reason: cancelData.cancelReason,
      }
    }

    try {
      await api.post('admin/appointment/cancel', payload)
      dispatch('showSuccessMessage', Vue.i18n.translate('appointment.messages.successfullyCanceled'))
      dispatch('fetchAppointmentAsAdmin', cancelData?.id)
      dispatch('fetchAppointmentsAsAdmin')
      commit('closeAllModals')
    } catch ({ data }) {
      data?.errors && data?.errors?.length >= 1
        ? dispatch('showErrorMessage', errorMessages(data.errors))
        : dispatch('showErrorMessage', data.message)
    } finally {
      commit('setAppointmentIsCanceling', false)
    }
  },

  async rescheduleAppointment({ commit, dispatch }, rescheduleData) {
    if (!rescheduleData?.id) {
      dispatch('showErrorMessage', Vue.i18n.translate('appointment.messages.appointmentNotFound'))
      return
    }

    await api
      .put(`${WEB_APPOINTMENT_URL}/reschedule/${rescheduleData?.id}`, rescheduleData)
      .then((response) => {
        if (response.status === 200) {
          commit('closeAllModals')
          dispatch('fetchBoard')
          dispatch('showSuccessMessage', Vue.i18n.translate('appointment.messages.rescheduled'))
        }
      })
      .catch(({ data }) => {
        commit('setIsLoading', false)
        data?.errors && data?.errors?.length >= 1
          ? dispatch('showErrorMessage', data.errors[0].message)
          : dispatch('showErrorMessage', data.message)
      })
  },

  async updateAppointmentDisplacement({ commit, dispatch }, { id, displacement }) {
    if (!id) {
      dispatch('showErrorMessage', Vue.i18n.translate('appointment.messages.appointmentNotFound'))
      return
    }

    await api
      .put(`${WEB_APPOINTMENT_URL}/${id}/displacement`, { displacement })
      .then((response) => {
        if (response.status === 200) {
          commit('closeAllModals')
          dispatch('fetchBoard')
          dispatch('showSuccessMessage', Vue.i18n.translate('appointment.adminActions.updateDisplacement.successMessage'))
        }
      })
      .catch(({ data }) => {
        commit('setIsLoading', false)
        data?.errors && data?.errors?.length >= 1
          ? dispatch('showErrorMessage', data.errors[0].message)
          : dispatch('showErrorMessage', data.message)
      })
  },

  changeAppointmentInspector({ commit, dispatch }, { appointmentId, inspectorId }) {
    if (!appointmentId) {
      dispatch('showErrorMessage', Vue.i18n.translate('appointment.messages.appointmentNotFound'))
      return Promise.resolve(false)
    }

    return api
      .post(`${WEB_APPOINTMENT_URL}/changeInspector`, {
        appointment_id: appointmentId,
        inspector_id: inspectorId,
      })
      .then(() => {
        commit('closeAllModals')
        dispatch('fetchBoard')
        dispatch('showSuccessMessage', Vue.i18n.translate('appointment.adminActions.changeInspector.successMessage'))
        return true
      })
      .catch(({ data }) => {
        data?.errors && data?.errors?.length >= 1
          ? dispatch('showErrorMessage', data.errors[0].message)
          : dispatch('showErrorMessage', data?.message)
        return false
      })
  },

  handleOpenBoardModal({ dispatch }, { appointmentId, modal }) {
    dispatch('openModal', modal)
    dispatch('fetchAppointment', appointmentId)
  },

  async fetchCancelReasons({ commit, dispatch }, isAdmin) {
    const cancelReasonsEndpoint = isAdmin ? '/admin/reasonCancel/list' : '/web/reasonCancel/list'

    try {
      const { data } = await api.get(cancelReasonsEndpoint)
      const reasons = sortAlphabetically(data, 'description')
      commit('setCancelReasons', setOtherOptionToEnd(reasons))
    } catch ({ data }) {
      data?.errors && data?.errors?.length >= 1
        ? dispatch('showErrorMessage', data.errors[0].message)
        : dispatch('showErrorMessage', data.message)
    }
  },

  async batchSchedule({ commit, getters }, body) {
    commit('setIsLoading', true)

    try {
      await appointmentService.batchSchedule(body).then(({ data }) => {
        handleBatchScheduleResponse(commit, getters, body, data)
      })
    } catch ({ data }) {
      handleBatchException(commit, getters, body, data)
    } finally {
      commit('setIsLoading', false)
    }
  },

  async batchPreschedule({ commit, getters }, body) {
    commit('setIsLoading', true)
    try {
      await appointmentService.batchPreschedule(body).then(({ data }) => {
        handleBatchScheduleResponse(commit, getters, body, data)
      })
    } catch ({ data }) {
      handleBatchException(commit, getters, body, data)
    } finally {
      commit('setIsLoading', false)
    }
  },

  async temporaryFileUpload({ commit }, payload) {
    try {
      await getSignedUrl({
        filename: payload.file.name,
        filetype: payload.file.type
      }, `${WEB_APPOINTMENT_URL}/temporaryFileUploadURL`)
        .then(async ({ signedUrl, external_filename}) => {
          await uploadToS3(signedUrl, payload.file)
            .then(() => {
              const attachData = {
                external_filename: external_filename,
                filename: payload.file.name,
              }

              return attachData
            })
            .then(async (attachData) => {
              await api.post(`${WEB_APPOINTMENT_URL}/addTemporaryFile`, attachData)
                .then(({ data }) => {
                  commit('setTemporaryFile', {id: data.id})
                })
            })
            .catch((error) => { console.log(error) })
          })
        .catch((error) => { console.log(error) })
    } catch (errors) {
      return errors
    }
  },

  async updateVisitorData({ commit, dispatch }, { visitorData, isAdmin }) {
    commit('setIsVisitorUpdating', true)

    try {
      isAdmin
        ? await appointmentService.updateVisitorAsAdmin(visitorData)
        : await appointmentService.updateVisitor(visitorData)

      dispatch('showSuccessMessage', Vue.i18n.translate('appointment.visitor.successfullyUpdated'))
      commit('closeAllModals')

      isAdmin
        ? dispatch('fetchAppointmentAsAdmin', visitorData?.appointment_id)
        : dispatch('fetchBoard')
    } catch ({data}) {
      data?.errors && data?.errors?.length >= 1
        ? dispatch('showErrorMessage', data.errors[0].message)
        : dispatch('showErrorMessage', data.message)
    } finally {
      commit('setIsVisitorUpdating', false)
    }
  },

  async updateFci({ commit, dispatch }, body) {
    commit('setIsFciUpdating', true)

    const fciData = {
      fci: body.fci,
      isAdmin: body.isAdmin,
      appointment_id: body.id,
    }

    try {
      await appointmentService.updateFci(fciData)
      const { data } = await appointmentService.show(body)
      commit('closeAllModals')
      commit('setAppointment', data)
      dispatch('showSuccessMessage',  Vue.i18n.translate('appointment.messages.fciUpdated'))
    } catch ({ data }) {
      data?.errors && data?.errors?.length >= 1
        ? dispatch('showErrorMessage', errorMessages(data.errors))
        : dispatch('showErrorMessage', data.message)
    } finally {
      commit('setIsFciUpdating', false)
    }
  },

  async replaceAppointmentTerm({ commit, dispatch }, { body, isAdmin }) {
    commit('setIsTermUploading', true)
    appointmentService.replaceTerm(body, isAdmin)
    .then(() => {
      isAdmin
        ? dispatch('fetchAppointmentFilesAsAdmin', body.appointment_id)
        : dispatch('fetchAppointment', body.appointment_id); dispatch('fetchAppointmentFiles', body.appointment_id)

      dispatch('showSuccessMessage',  Vue.i18n.translate('appointment.messages.termReplaced'))
      commit('closeAllModals')
    })
    .catch(({ data }) => {
      data?.errors && data?.errors?.length >= 1
        ? dispatch('showErrorMessage', errorMessages(data.errors))
        : dispatch('showErrorMessage', data.message)
    })
    .finally(() => commit('setIsTermUploading', false))
  },

  async addTagsToAppointment({ commit, dispatch }, body) {
    commit('setIsLoading', true)
    appointmentService.addTags(body)
    .then(() => {
      dispatch('fetchDashboardAppointments')
      dispatch('showSuccessMessage',  Vue.i18n.translate('appointment.messages.addedTags'))
    })
    .catch(({ data }) => {
      data?.errors && data?.errors?.length >= 1
        ? dispatch('showErrorMessage', errorMessages(data.errors))
        : dispatch('showErrorMessage', data.message)
    })
    .finally(() => commit('setIsLoading', false))
  },

  async removeTagsFromAppointment({ commit, dispatch }, body) {
    commit('setIsLoading', true)
    appointmentService.removeTags(body)
    .then(() => {
      dispatch('fetchDashboardAppointments')
      dispatch('showSuccessMessage',  Vue.i18n.translate('appointment.messages.removedTags'))
    })
    .catch(({ data }) => {
      data?.errors && data?.errors?.length >= 1
        ? dispatch('showErrorMessage', errorMessages(data.errors))
        : dispatch('showErrorMessage', data.message)
    })
    .finally(() => commit('setIsLoading', false))
  },

  async fetchConfirmAppointmentData({ commit, dispatch }, id) {
    commit('setIsLoading', true)

    try {
      const { data } = await api.get(`${OPEN_APPOINTMENT_URL}/show/${id}`)
      commit('setConfirmAppointmentData', data)
    } catch ({ data }) {
      dispatch('showErrorMessage', data.message)
    } finally {
      commit('setIsLoading', false)
    }
  },

  async confirmationSignatureUploadUrl({ dispatch }, { appointment_id, filename, filetype }) {
    try {
      const { data } = await api.post(`${OPEN_APPOINTMENT_URL}/confirmationSignatureUploadUrl`, {
        appointment_id,
        filename,
        filetype,
      })

      return data
    } catch ({ data }) {
      dispatch('showErrorMessage', data.message)
    }
  },

  async addAppointmentConfirmation({ dispatch }, { body, isManual }) {
    const userRoles = localStorage.getItem('access_roles')
    let isAdminOrPartner = false
    if (userRoles) {
      const userRolesArray = userRoles?.split(',')
      isAdminOrPartner = userRolesArray.includes('ROLE_ADMIN') || userRolesArray.includes('ROLE_PARTNER')
    }

    // A imobiliária confirma manualmente pela rota do portal, que valida o vínculo do agendamento
    // com a imobiliária logada; a Hauseful segue usando a rota do backoffice.
    const manualConfirmationEndpoint = isAdminOrPartner
      ? `${ADMIN_APPOINTMENT_URL}/manualConfirmation`
      : `${WEB_APPOINTMENT_URL}/manualConfirmation`
    const defaultConfirmationEndpoint = `${OPEN_APPOINTMENT_URL}/addAppointmentConfirmation`
    const endpoint = isManual ? manualConfirmationEndpoint : defaultConfirmationEndpoint

    try {
      await api.post(endpoint, body)
      dispatch('showSuccessMessage', Vue.i18n.translate('appointment.confirmAppointment.messages.success'))
      await dispatch('fetchConfirmAppointmentData', body.appointment_id)
      if (isAdminOrPartner) {
        await dispatch('fetchAppointmentAsAdmin', body.appointment_id)
      }
      return true
    } catch({ data }) {
      dispatch('showErrorMessage', data.message)
    }
  },

  async fetchChecklistLinkedAppointmentTypes({ commit, dispatch }) {
    commit('setIsLoading', true)
    appointmentService.getChecklistLinkedAppointmentTypes()
      .then(({ data }) => {
        commit('setChecklistLinkedAppointmentTypes', data)
      })
      .catch(({ data }) => {
        data?.errors && data?.errors?.length >= 1
          ? dispatch('showErrorMessage', data.errors[0].message)
          : dispatch('showErrorMessage', data.message)
      })
      .finally(() => {
        commit('setIsLoading', false)
      })
  },

  async downloadRealEstateAppointmentExcel({ commit, dispatch, rootState }) {
    commit('setIsLoading', true)
    try {
      const filters = (rootState?.filters?.filters) || {}
      const { size, page, ...params } = filters
      const response = await appointmentService.generateRealEstateAppointmentExcel(params)

      const cd = response?.headers?.['content-disposition'] || response?.headers?.['Content-Disposition']
      let filename = 'Agendamentos.xlsx'
      if (cd) {
        const match = cd.match(/filename\*?=(?:UTF-8'')?"?([^"]+)"?/i)
        if (match?.[1]) {
          try { filename = decodeURIComponent(match[1]) } catch (_) { filename = match[1] }
          if (!/\.xlsx$/i.test(filename)) filename += '.xlsx'
        }
      }

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (e) {
      dispatch('showErrorMessage', e?.message || 'Falha ao gerar Excel')
    } finally {
      commit('setIsLoading', false)
    }
  },
}

const mutations = {
  setAppointment: (state, appointment) => {
    state.appointment = appointment
  },

  setAppointments: (state, appointments) => {
    state.appointments = appointments
  },

  setCalendarAppointments: (state, calendarAppointments) => {
    state.calendarAppointments = calendarAppointments
  },

  setCalendarLoading: (state, isCalendarLoading) => {
    state.isCalendarLoading = isCalendarLoading
  },

  setWorkers: (state, workers) => {
    state.workers = {
      hosts: workers?.hosts || [],
      internals: workers?.internals || [],
      thirdParties: workers?.thirdParties || [],
    }
  },

  setAppointmentTypes: (state, appointmentTypes) => {
    state.appointmentTypes = appointmentTypes
  },

  setRealEstateAvailableServiceTypes: (state, types) => {
    state.realEstateAvailableServiceTypes = types
  },

  setCancelReasons: (state, cancelReasons) => {
    state.cancelReasons = cancelReasons
  },

  setAppointmentCreationConfirmToClose: (
    state,
    appointmentCreationConfirmToClose
  ) => {
    state.appointmentCreationConfirmToClose = appointmentCreationConfirmToClose
  },

  setBatchAppointments: (state, batchAppointments) => {
    state.batchAppointments = batchAppointments
  },

  setBatchAppointmentsWithError: (state, batchAppointmentsWithError) => {
    state.batchAppointmentsWithError = batchAppointmentsWithError
  },

  setIsAppointmentLoading: (state, status) => {
    state.isAppointmentLoading = status
  },

  setIsVisitorUpdating: (state, status) => {
    state.isVisitorUpdating = status
  },

  setIsFciUpdating: (state, status) => {
    state.isFciUpdating = status
  },

  setIsTermUploading(state, status) {
    state.isTermUploading = status
  },

  setAppointmentIsCanceling: (state, status) => {
    state.isAppointmentCanceling = status
  },

  setConfirmAppointmentData: (state, data) => {
    state.confirmAppointmentData = data
  },

  setTemporaryFile: (state, data) => {
    state.temporaryFile = data
  },

  setChecklistLinkedAppointmentTypes: (state, data) => {
    state.checklistLinkedAppointmentTypes = data
  },
}

const handleBatchScheduleResponse = (commit, getters, body, data) => {
  let list = getters.batchAppointments
  if (data.error) {
    data.appointment = {
      date: body.date,
      type: body.tipo,
    }
    let errorList = getters.batchAppointmentsWithError
    errorList.push(data)
    commit('setBatchAppointmentsWithError', errorList)
    return
  }
  list.push(data)
  commit('setBatchAppointments', list)
}

const handleBatchException = (commit, getters, body, data) => {
  if (data) {
    let errorMessage = null
    if (data.errors && data.errors.length >= 1) {
      errorMessage = data.errors[0].message
    } else {
      errorMessage = data.message
    }

    let list = getters.batchAppointmentsWithError

    data.index = body.index
    data.appointment = {
      date: body.date,
      type: body.tipo,
    }
    data.error = {
      message: errorMessage,
    }
    list.push(data)
    commit('setBatchAppointmentsWithError', list)
  }
}

export default {
  state,
  mutations,
  actions,
  getters,
}
